import React from "react";
import { useQueryClient } from "@tanstack/react-query";

import * as types from "types";
import useMessagesSuspenseQuery from "services/dbApi/hooks/useMessagesSuspenseQuery";
import useCommitMessages from "services/dbApi/hooks/useCommitMessages";
import { PRODUCT_NAME } from "utilities/productName";

type MessagesAction =
  | types.Message[]
  | ((prev: types.Message[]) => types.Message[]);

export type UseCurrentMessagesReturn = readonly [
  messages: types.Message[],
  /**
   * Updates the messages list in-memory.
   * Messages are held in the React Query cache.
   * Use for rapid changes (e.g. each token of a streaming chat completion).
   */
  setMessages: (action: MessagesAction) => void,
  /**
   * Updates the messages list in-memory AND in the database.
   * Database persistence through `PUT /threads/:id/messages` (a snapshot
   * replace). Use to commit a final value (e.g. once a streaming completion
   * has finished, or after a file is uploaded).
   */
  commitMessages: (action: MessagesAction) => void,
  /**
   * Refetches the messages list from the backend, replacing the in-memory
   * cache with the server's snapshot. Use to pull in changes made by other
   * tabs/clients (e.g. on tab refocus).
   */
  refetchMessages: () => void
];

/**
 * Reads the current thread's messages from the React Query cache and
 * returns useState-style writers for them. The cache is the single source
 * of truth — `setMessages` updates it locally, `commitMessages` updates it
 * locally AND persists to the backend.
 *
 * Suspends while the messages list is loading.
 */
export function useCurrentMessages(threadId: string): UseCurrentMessagesReturn {
  const queryClient = useQueryClient();
  const saveMessages = useCommitMessages({
    errorTitle: "Couldn't save your messages",
    errorDetail: `${PRODUCT_NAME} couldn't save your latest messages after several attempts. Nothing already saved is affected.`,
  });

  const messagesQuery = useMessagesSuspenseQuery({ threadId });
  const messages = messagesQuery.data.messages;

  const setMessages = React.useCallback(
    (action: MessagesAction) => {
      queryClient.setQueryData<{ messages: types.Message[] }>(
        ["cc-db-api", "threads", threadId, "messages"],
        (prev) => {
          const prevList = prev?.messages ?? [];
          const next = typeof action === "function" ? action(prevList) : action;
          return { messages: next };
        }
      );
    },
    [queryClient, threadId]
  );

  const commitMessages = React.useCallback(
    (action: MessagesAction) => {
      const prevList =
        queryClient.getQueryData<{ messages: types.Message[] }>([
          "cc-db-api",
          "threads",
          threadId,
          "messages",
        ])?.messages ?? [];
      const newMessages =
        typeof action === "function" ? action(prevList) : action;
      // `saveMessages` never rejects: a failed PUT throws a MessagesWriteError
      // during render instead (ADR 065).
      void saveMessages(threadId, newMessages);
    },
    [queryClient, threadId, saveMessages]
  );

  const refetchMessages = React.useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["cc-db-api", "threads", threadId, "messages"],
    });
  }, [queryClient, threadId]);

  return [messages, setMessages, commitMessages, refetchMessages] as const;
}
